import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, ArrowRight, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const schema = z.object({
  email:    z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export default function Login() {
  const { login } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const from      = location.state?.from?.pathname || '/dashboard';

  const [showPw, setShowPw]   = useState(false);
  const [apiErr, setApiErr]   = useState('');

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
  });

  async function onSubmit(data) {
    setApiErr('');
    try {
      await login(data.email, data.password);
      navigate(from, { replace: true });
    } catch (err) {
      setApiErr(err.response?.data?.error?.message || 'Login failed. Please try again.');
    }
  }

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to your booking dashboard"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {apiErr && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2.5 text-sm">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{apiErr}</span>
          </div>
        )}

        <div>
          <label className="label">Email</label>
          <input
            {...register('email')}
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className={`input ${errors.email ? 'border-red-400' : ''}`}
          />
          {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="label mb-0">Password</label>
            <Link to="/forgot-password" className="text-xs text-primary hover:underline">Forgot password?</Link>
          </div>
          <div className="relative">
            <input
              {...register('password')}
              type={showPw ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              className={`input pr-10 ${errors.password ? 'border-red-400' : ''}`}
            />
            <button type="button" onClick={() => setShowPw(p => !p)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink">
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>}
        </div>

        <button type="submit" disabled={isSubmitting} className="btn-primary w-full justify-center py-2.5 mt-2">
          {isSubmitting ? 'Signing in…' : (
            <><span>Sign in</span><ArrowRight size={16} /></>
          )}
        </button>
      </form>

      <p className="text-center text-sm text-ink-secondary mt-6">
        Don't have an account?{' '}
        <Link to="/signup" className="text-primary font-medium hover:underline">Create one free</Link>
      </p>
    </AuthShell>
  );
}

// Shared auth shell layout
export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-surface-50 flex">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between w-[420px] shrink-0 bg-slate-900 p-10">
        <div>
          <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center mb-8">
            <span className="text-white font-bold text-lg">B</span>
          </div>
          <h2 className="text-white text-2xl font-semibold leading-tight mb-3">
            The booking platform built for local businesses
          </h2>
          <p className="text-slate-400 text-sm leading-relaxed">
            Manage appointments, staff, and clients — all from one beautiful dashboard.
            Embed a booking widget on any website in minutes.
          </p>
        </div>

        <div className="space-y-4">
          {[
            { stat: '2,400+', label: 'Businesses powered' },
            { stat: '98%',    label: 'Uptime SLA' },
            { stat: '< 2 min', label: 'Setup time' },
          ].map(item => (
            <div key={item.label} className="flex items-center gap-4">
              <span className="text-white font-semibold text-lg w-24">{item.stat}</span>
              <span className="text-slate-400 text-sm">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">B</span>
            </div>
            <span className="font-semibold text-ink">Booking Platform</span>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-ink tracking-tight">{title}</h1>
            {subtitle && <p className="text-ink-secondary text-sm mt-1">{subtitle}</p>}
          </div>

          {children}
        </div>
      </div>
    </div>
  );
}
